import React, { useState, useEffect } from "react";
import { Modal, Tabs } from "flowbite-react";
import { Transaction } from "../../../domain/types/Transaction";
import { formatCurrency } from "../utils/transactionFormatters";
import { TransactionActions } from "./TransactionActions";
import { BankAccount } from "../../../domain/types/banks/BankAccount";
import { Bank } from "../../../domain/types/banks/Bank";
import {
  HiOutlineCalendar,
  HiOutlineBanknotes,
  HiOutlineUserCircle,
  HiOutlineCreditCard,
  HiOutlineIdentification,
} from "react-icons/hi2";
import { HiOutlineExternalLink, HiOutlineLocationMarker } from "react-icons/hi";

// Import Leaflet for map display
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// Fix default icon issue with Leaflet in React
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

interface TransactionDetailsProps {
  transaction: Transaction;
  account: BankAccount | null;
  bank: Bank | null;
  onClose: () => void;
}

export const TransactionDetails: React.FC<TransactionDetailsProps> = ({
  transaction,
  account,
  bank,
  onClose,
}) => {
  const details = transaction.transaction_details || {};
  const [mapUrl, setMapUrl] = useState<string | null>(null);
  const [coordinates, setCoordinates] = useState<{
    lat: number;
    lng: number;
  } | null>(null);

  const hasLocation = !!(
    details.location &&
    (details.location.address ||
      details.location.city ||
      details.location.region ||
      details.location.country)
  );

  useEffect(() => {
    const fetchLocationData = async () => {
      if (hasLocation) {
        const query = encodeURIComponent(
          `${details.location.address || ""} ${details.location.city || ""} ${
            details.location.region || ""
          } ${details.location.country || ""}`,
        );
        // Use Nominatim API to get coordinates
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${query}&format=json&limit=1`,
        );
        const data = await response.json();
        if (data && data.length > 0) {
          const { lat, lon } = data[0];
          setCoordinates({ lat: parseFloat(lat), lng: parseFloat(lon) });

          setMapUrl(
            `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lon}#map=16/${lat}/${lon}`,
          );
        }
      }
    };

    fetchLocationData();
  }, [details.location, hasLocation]);

  return (
    <Modal
      show
      onClose={onClose}
      size="lg"
      dismissible={true}
      aria-label="Transaction Details"
      className="flex items-center justify-center"
    >
      <div className="relative w-full max-w-lg max-h-full">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <Modal.Header>
            <div className="flex items-center">
              <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
                Transaction Details
              </h2>
            </div>
          </Modal.Header>
          <Modal.Body className="max-h-[70vh] overflow-y-auto">
            <Tabs aria-label="Transaction Tabs">
              <Tabs.Item title="Transaction">
                <TransactionInfo
                  transaction={transaction}
                  hasLocation={hasLocation}
                  mapUrl={mapUrl}
                  coordinates={coordinates}
                />
              </Tabs.Item>
              <Tabs.Item title="Account">
                <AccountInfo account={account} bank={bank} />
              </Tabs.Item>
            </Tabs>
          </Modal.Body>
        </div>
      </div>
    </Modal>
  );
};

const TransactionInfo: React.FC<{
  transaction: Transaction;
  hasLocation: boolean;
  mapUrl: string | null;
  coordinates: { lat: number; lng: number } | null;
}> = ({ transaction, hasLocation, mapUrl, coordinates }) => {
  const details = transaction.transaction_details || {};
  const merchant = details.counterparties?.[0];

  return (
    <div className="space-y-8">
      {/* Merchant Information */}
      {merchant && (
        <section aria-labelledby="merchant-information">
          <h3 id="merchant-information" className="text-lg font-semibold mb-4">
            Merchant Information
          </h3>
          <div className="flex items-center space-x-4">
            {merchant.logo_url ? (
              <img
                src={merchant.logo_url}
                alt={`${merchant.name} Logo`}
                className="w-16 h-16 rounded-full border border-gray-200 dark:border-gray-700"
              />
            ) : (
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
                <HiOutlineUserCircle className="text-gray-400 dark:text-gray-500 w-8 h-8" />
              </div>
            )}
            <div>
              <div className="text-xl font-semibold text-gray-800 dark:text-gray-200">
                {merchant.name}
              </div>
              {merchant.website && (
                <a
                  href={merchant.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-200 flex items-center"
                >
                  Visit Website
                  <HiOutlineExternalLink className="ml-1 w-4 h-4" />
                </a>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Transaction Details */}
      <section aria-labelledby="transaction-details">
        <h3 id="transaction-details" className="text-lg font-semibold mb-4">
          Transaction Details
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <DetailItem
            icon={HiOutlineCalendar}
            label="Date"
            value={new Date(transaction.transaction_date).toLocaleDateString()}
          />
          <DetailItem
            icon={HiOutlineCreditCard}
            label="Type"
            value={transaction.transaction_type || "Unknown"}
          />
          <DetailItem
            icon={HiOutlineBanknotes}
            label="Amount"
            value={formatCurrency(transaction.amount)}
            valueClassName={getTextColor(transaction.transaction_type)}
          />
          <DetailItem
            icon={HiOutlineIdentification}
            label="Category"
            value={transaction.category_description || "N/A"}
            valueClassName="whitespace-nowrap"
          />
          {details.payment_channel && (
            <DetailItem
              icon={HiOutlineCreditCard}
              label="Payment Method"
              value={details.payment_channel}
            />
          )}
        </div>
      </section>

      {/* Transaction Location */}
      {hasLocation && coordinates && (
        <section aria-labelledby="transaction-location">
          <h3 id="transaction-location" className="text-lg font-semibold mb-4">
            Location
          </h3>
          <div className="flex items-center space-x-4">
            <HiOutlineLocationMarker className="w-6 h-6 text-gray-600 dark:text-gray-300" />
            <div className="text-base text-gray-800 dark:text-gray-200">
              {formatLocation(details.location)}
            </div>
          </div>
          <div className="mt-4">
            {/* Display the map using react-leaflet */}
            <MapContainer
              center={[coordinates.lat, coordinates.lng]}
              zoom={16}
              scrollWheelZoom={false}
              style={{ height: "300px", width: "100%" }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <Marker position={[coordinates.lat, coordinates.lng]}>
                <Popup>{merchant?.name || "Location"}</Popup>
              </Marker>
            </MapContainer>
          </div>
          <div className="mt-2">
            {mapUrl && (
              <a
                href={mapUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-200 flex items-center"
              >
                View on OpenStreetMap
                <HiOutlineExternalLink className="ml-1 w-4 h-4" />
              </a>
            )}
          </div>
        </section>
      )}

      {/* Actions */}
      <section
        aria-labelledby="transaction-actions"
        className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700"
      >
        <TransactionActions transaction={transaction} expanded />
      </section>
    </div>
  );
};

const AccountInfo: React.FC<{
  account: BankAccount | null;
  bank: Bank | null;
}> = ({ account, bank }) => {
  if (!account) {
    return <div>No account information available.</div>;
  }

  return (
    <div className="space-y-8">
      {/* Bank Information */}
      <section aria-labelledby="bank-information">
        <h3 id="bank-information" className="text-lg font-semibold mb-4">
          Bank Information
        </h3>
        <div className="flex items-center space-x-4">
          {bank?.logo ? (
            <img
              src={`data:image/png;base64, ${bank.logo}`}
              alt={`${bank.name} Logo`}
              className="w-16 h-16 rounded-full border border-gray-200 dark:border-gray-700"
            />
          ) : (
            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
              <HiOutlineBanknotes className="text-gray-400 dark:text-gray-500 w-8 h-8" />
            </div>
          )}
          <div>
            <h4 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
              {bank?.name || "Unknown Bank"}
            </h4>
            {bank?.url && (
              <a
                href={bank.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-200 flex items-center"
              >
                Visit Bank Website
                <HiOutlineExternalLink className="ml-1 w-4 h-4" />
              </a>
            )}
          </div>
        </div>
      </section>

      {/* Account Details */}
      <section aria-labelledby="account-details">
        <h3 id="account-details" className="text-lg font-semibold mb-4">
          Account Details
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <DetailItem
            icon={HiOutlineCreditCard}
            label="Account Name"
            value={account.name}
          />
          <DetailItem
            icon={HiOutlineCreditCard}
            label="Type"
            value={account.type}
          />
          {account.subType && (
            <DetailItem
              icon={HiOutlineCreditCard}
              label="Sub-Type"
              value={account.subType}
            />
          )}
          {account.mask && (
            <DetailItem
              icon={HiOutlineCreditCard}
              label="Account Number"
              value={`**** **** **** ${account.mask}`}
            />
          )}
        </div>
      </section>
    </div>
  );
};

// Include the DetailItem component as requested
const DetailItem: React.FC<{
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  valueClassName?: string;
}> = ({ icon: Icon, label, value, valueClassName }) => (
  <div className="flex items-start space-x-3">
    <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-full">
      <Icon
        className="w-5 h-5 text-gray-600 dark:text-gray-300"
        aria-hidden="true"
      />
    </div>
    <div>
      <div className="text-sm text-gray-500 dark:text-gray-400">{label}</div>
      <div
        className={`text-base text-gray-800 dark:text-gray-200 ${valueClassName || ""}`}
      >
        {value}
      </div>
    </div>
  </div>
);

const formatLocation = (location: any) => {
  const parts = [];
  if (location.address) parts.push(location.address);
  if (location.city) parts.push(location.city);
  if (location.region) parts.push(location.region);
  if (location.country) parts.push(location.country);
  return parts.join(", ");
};

const getTextColor = (type: string | undefined): string => {
  switch (type?.toLowerCase()) {
    case "income":
      return "text-green-600 dark:text-green-400";
    case "expense":
      return "text-red-600 dark:text-red-400";
    default:
      return "";
  }
};

export default TransactionDetails;
